/**
 * FoodImage — drop-in `<img>` for restaurant photos with:
 *   • Native lazy loading (`loading="lazy"`)
 *   • Responsive `srcSet` via Cloudinary URL transformations
 *   • Skeleton shimmer while loading
 *   • Graceful broken-image fallback (gradient + utensils icon)
 *   • `object-cover` — never stretch, never distort
 *
 * Variants control the responsive widths set we emit:
 *   thumb   — 80–120px (menu card left thumbnail)
 *   card    — 320–480px (directory tile, fast spotlight)
 *   detail  — 720–1080px (product detail modal)
 *   hero    — 1080–1600px (restaurant cover, full screen)
 *
 * Always use `<FoodImage>` instead of raw `<img>` for any food / cover /
 * logo / category photo so the platform behaves consistently across
 * devices + connections.
 */
import React, { useState } from 'react';
import { UtensilsCrossed } from 'lucide-react';
import { srcSetFor, cloudinaryUrl } from '../utils/cloudinaryUrl';

const WIDTH_SETS = {
  thumb:  [80, 160, 240],
  card:   [240, 320, 480, 640],
  detail: [480, 720, 1080],
  hero:   [720, 1080, 1440, 1920],
};

const SIZES = {
  thumb:  '(max-width: 640px) 72px, 96px',
  card:   '(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 320px',
  detail: '(max-width: 640px) 100vw, 640px',
  hero:   '100vw',
};

const FoodImage = ({
  src,
  alt = '',
  variant = 'card',
  className = '',
  aspectClass = '',
  rounded = '',
  testid,
  fallback,
  ...rest
}) => {
  const [errored, setErrored] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const hasSrc = Boolean(src) && !errored;

  const widths = WIDTH_SETS[variant] || WIDTH_SETS.card;
  const sizes = SIZES[variant] || SIZES.card;
  // Display size — what the browser should download initially. We use
  // the smallest width in the set; the browser upgrades via srcSet on
  // wide screens.
  const baseUrl = hasSrc ? cloudinaryUrl(src, { w: widths[0] }) : null;
  const srcSet = hasSrc ? srcSetFor(src, widths) : null;

  const wrapCls = `relative ${aspectClass} ${rounded} overflow-hidden bg-slate-100 ${className}`.trim();

  if (!hasSrc) {
    return (
      <div className={wrapCls} data-testid={testid}>
        {fallback || (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100">
            <UtensilsCrossed className="w-1/3 h-1/3 max-w-[40px] max-h-[40px] text-slate-300" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={wrapCls} data-testid={testid}>
      {!loaded && (
        <div
          className="absolute inset-0 bg-gradient-to-br from-slate-100 via-slate-200 to-slate-100 animate-pulse"
          aria-hidden="true"
        />
      )}
      <img
        src={baseUrl}
        srcSet={srcSet || undefined}
        sizes={sizes}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => setErrored(true)}
        className={`absolute inset-0 w-full h-full object-cover ${loaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-200`}
        {...rest}
      />
    </div>
  );
};

export default FoodImage;
